import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ApiService } from 'src/app/core/services/api.service';
import { PaginationService } from 'src/app/core/services/pagination.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-price-histories',
  standalone: false,
  templateUrl: './price-histories.component.html',
  styleUrl: './price-histories.component.scss'
})
export class PriceHistoriesComponent {
 breadCrumbItems!: Array<{}>;
  priceHistoryForm!: UntypedFormGroup;
  submitted = false;
  showForm = false;
  isEditMode = false;
  editId: any = null;

  priceHistories: any[] = [];
  items: any[] = [];
  suppliers: any[] = [];
  totalItems: any;
  isLoading = false;

  // Pagination
  page = 1;
  pageSize = 10;

  constructor(
    private fb: UntypedFormBuilder,
    private api: ApiService,
    private paginationService: PaginationService
  ) {}

  ngOnInit(): void {
    this.breadCrumbItems = [
      { label: 'Inventory' },
      { label: 'Item Prices', active: true }
    ];
    this.initializeForm();
    this.loadPriceHistories();
    this.loadItems();
    this.loadSuppliers();
  }

  initializeForm(): void {
    this.priceHistoryForm = this.fb.group({
      item_id: ['', Validators.required],
      supplier_id: ['', Validators.required],
      price: [0, [Validators.required, Validators.min(0)]],
      effective_date: ['', Validators.required],
      remarks: ['']
    });
  }

  get f() { return this.priceHistoryForm.controls; }

  toggleForm(): void {
    this.showForm = !this.showForm;
    if (!this.showForm) this.resetForm();
  }

  resetForm(): void {
    this.submitted = false;
    this.isEditMode = false;
    this.editId = null;
    this.priceHistoryForm.reset({
      item_id: '',
      supplier_id: '',
      price: 0,
      effective_date: '',
      remarks: ''
    });
  }

  loadPriceHistories(): void {
    this.isLoading = true;
    this.api.get(`/price_histories?page=${this.page}&limit=${this.pageSize}`).subscribe({
      next: (res: any) => {
        this.isLoading = false;
        if (res.success) {
          this.priceHistories = res.data || [];
          this.totalItems = Number(res.total) || 0;
          this.pageSize = Number(res.perPage) || this.pageSize;
          this.page = Number(res.currentPage) || this.page;
        }
      },
      error: () => (this.isLoading = false)
    });
  }

  loadItems(): void {
    this.api.get('/items').subscribe({
      next: (res: any) => {
        if (res.success) this.items = res.data;
      }
    });
  }

  loadSuppliers(): void {
    this.api.get('/suppliers').subscribe({
      next: (res: any) => {
        if (res.success) this.suppliers = res.data;
      }
    });
  }

  savePriceHistory(): void {
    this.submitted = true;
    if (this.priceHistoryForm.invalid) return;

    const formValue = this.priceHistoryForm.value;
    const payload = {
      item_id: formValue.item_id,
      supplier_id: formValue.supplier_id,
      price: formValue.price,
      effective_date: formValue.effective_date,
      remarks: formValue.remarks
    };

    if (this.isEditMode && this.editId) {
      this.api.put(`/price_histories/${this.editId}`, payload).subscribe({
        next: (res: any) => {
          if (res.success) {
            Swal.fire('Success', 'Price history updated successfully', 'success');
            this.loadPriceHistories();
            this.toggleForm();
          }
        }
      });
    } else {
      this.api.post('/price_histories', payload).subscribe({
        next: (res: any) => {
          if (res.success) {
            Swal.fire('Success', 'Price history added successfully', 'success');
            this.loadPriceHistories();
            this.toggleForm();
          }
        }
      });
    }
  }

  editPriceHistory(history: any): void {
  this.showForm = true;
  this.isEditMode = true;
  this.editId = history.id;

  this.priceHistoryForm.patchValue({
    item_id: history.itemId,
    supplier_id: history.supplierId,
    price: history.price,
    effective_date: history.effectiveDate, 
    remarks: history.remarks
  });
}

  deletePriceHistory(id: number): void {
    Swal.fire({
      title: 'Are you sure?',
      text: 'You will not be able to recover this record!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it!',
    }).then(result => {
      if (result.isConfirmed) {
        this.api.delete(`/price_histories/${id}`).subscribe({
          next: (res: any) => {
            if (res.success) {
              Swal.fire('Deleted!', 'Price history deleted successfully', 'success');
              this.loadPriceHistories();
            }
          }
        });
      }
    });
  }

  changePage(page: number): void {
    this.page = page;
    this.loadPriceHistories();
  }

  getMin(a: number, b: number): number {
    return Math.min(a, b);
  }
}